
import links_common from "../core/relations/links_common";
import graph_helper from "../core/relations/graph_helper";

import LinksBuilder from "../core/relations/links_builder";
import {ConstraintTypes} from "./auto_scheduling/constraint_types";
import {ConstraintsHelper} from "./auto_scheduling/constraints";
import {AutoSchedulingPlanner} from "./auto_scheduling/planner";
import {ConnectedGroupsHelper} from "./auto_scheduling/connected_groups";
import {LoopsFinder} from "./auto_scheduling/loops_finder";

import ConfigBuilder from "../core/plugins/auto_scheduling_config";

import {attachUIHandlers} from "./auto_scheduling/ui_handlers";

// Phase C Day 13: v2 dispatch entries. Each method below is wrapped at the
// bottom of the extension init so calls route to v2 when
// `gantt.config.auto_scheduling._engine === "v2"`. Default stays "v1"; the
// v2 engine code is opt-in for Phase C / Phase D soak.
import {
	autoScheduleViaEngine,
	getOrBuildSnapshot,
	attachAutoScheduleTriggers,
	wireRevisionTracker
} from "../scheduling/adapters/gantt_adapter";
import {
	buildAnalysisGraph as _v2_buildAnalysisGraph,
	findCycles as _v2_findCycles,
	findCycleIfAdded as _v2_findCycleIfAdded,
	getSnapshotLink as _v2_getSnapshotLink,
	connectedComponents as _v2_connectedComponents,
	resolveConstraintType as _v2_resolveConstraintType,
	getConstraintLimits as _v2_getConstraintLimits
} from "../scheduling/core/api";
import { buildCandidateLinkSnapshot as _v2_buildCandidateLinkSnapshot } from "../scheduling/adapters/link_snapshot";

export default function(gantt){

	const { getDefaultAutoSchedulingConfig, getAutoSchedulingConfig} = ConfigBuilder(gantt);

	links_common(gantt);

var linksBuilder = LinksBuilder(gantt);
var graphHelper = graph_helper(gantt);


var constraintsHelper = ConstraintsHelper.Create(
	gantt
);


var planner = new AutoSchedulingPlanner(gantt, graphHelper, constraintsHelper);

var connectedGroups = new ConnectedGroupsHelper(gantt, linksBuilder);

var loopsFinder = new LoopsFinder(
	gantt,
	graphHelper,
	linksBuilder
);

gantt.getConnectedGroup = connectedGroups.getConnectedGroup;
gantt.getConstraintType = constraintsHelper.getConstraintType;
gantt.getConstraintLimitations = function (task) {
	var plan = constraintsHelper.processConstraint(task, null);
	if (plan) {
		return {
			earliestStart: plan.earliestStart || null,
			earliestEnd: plan.earliestEnd || null,
			latestStart: plan.latestStart || null,
			latestEnd: plan.latestEnd || null
		};
	} else {
		return {
			earliestStart: null,
			earliestEnd: null,
			latestStart: null,
			latestEnd: null
		};
	}
};

gantt.isCircularLink = loopsFinder.isCircularLink;
gantt.findCycles = loopsFinder.findCycles;

gantt.config.auto_scheduling = getDefaultAutoSchedulingConfig();
gantt.config.constraint_types = ConstraintTypes;

function preferInitialTaskDates(startTask, relations) {
	if (gantt._getAutoSchedulingConfig().apply_constraints) {
		return;
	}

	// .preferredStart exists only to emulate pre 6.1 auto scheduling behavior
	for (var i = 0; i < relations.length; i++) {
		var rel = relations[i];
		var task = gantt.getTask(rel.target);

		if ((gantt._getAutoSchedulingConfig().gap_behavior === "preserve") || rel.target == startTask) {
			rel.preferredStart = new Date(task.start_date);
		}
	}
}

var afterRenderBeforeFirstRepaint = false;
gantt.attachEvent("onParse", function(){
	afterRenderBeforeFirstRepaint = true;
	return true;
});

gantt.attachEvent("onBeforeGanttRender", function(){
	afterRenderBeforeFirstRepaint = false;
	return true;
});

function updateParentsAndCallEvents(updatedTasks) {
	if(!updatedTasks.length){
		return;
	}

	gantt.batchUpdate(function payload() {
		for (var i = 0; i < updatedTasks.length; i++) {
			gantt.updateTask(updatedTasks[i]);
		}
	}, afterRenderBeforeFirstRepaint); // don't call repaint if auto scheduling on parse before initial repain
}

gantt._autoSchedule = function (id, relations) {
	if (gantt.callEvent("onBeforeAutoSchedule", [id]) === false) {
		return;
	}
	gantt._autoscheduling_in_progress = true;

	var constraints = constraintsHelper.getConstraints(
		id,
		gantt.isTaskExists(id) ? relations : null
	);

	var updatedTasks = [];

	var cycles = graphHelper.findLoops(relations);
	if (cycles.length) {
		gantt.callEvent("onAutoScheduleCircularLink", [cycles]);
	} else {
		preferInitialTaskDates(id, relations);

		// GS-2427. Auto-schedule twice when there are links with project tasks
		// as we cannot be sure that the project duration doesn't change
		for (let i = 0; i < relations.length; i++) {
			if (relations[i].subtaskLink){
				planner._secondIterationRequired = true;
				planner._secondIteration = false;
				break;
			}
		}

		var plan = planner.generatePlan(relations, constraints);
		updatedTasks = planner.applyProjectPlan(plan);

		updateParentsAndCallEvents(updatedTasks);
	}

	gantt._autoscheduling_in_progress = false;
	gantt.callEvent("onAfterAutoSchedule", [id, updatedTasks]);
};

gantt.autoSchedule = function (id, inclusive) {
	if (inclusive === undefined) {
		inclusive = true;
	} else {
		inclusive = !!inclusive;
	}

	var relations;
	if (id !== undefined) {
		if (!gantt._getAutoSchedulingConfig().apply_constraints) {
			relations = linksBuilder.getLinkedTasks(id, inclusive);
		} else {
			relations = connectedGroups.getConnectedGroupRelations(id);
		}
	} else {
		relations = linksBuilder.getLinkedTasks();
	}

	gantt._autoSchedule(id, relations);
};

gantt.attachEvent("onTaskLoading", function (task) {
	if (task.constraint_date && typeof task.constraint_date === "string") {
		task.constraint_date = gantt.date.parseDate(task.constraint_date, "parse_date");
	}
	task.constraint_type = gantt.getConstraintType(task);
	return true;
});
gantt.attachEvent("onTaskCreated", function (task) {
	task.constraint_type = gantt.getConstraintType(task);
	return true;
});

// Phase D Day 1 — init-time exclusive trigger attach.
// `_engine === "v2"` → v2 triggers (anchor-cache aware, drives D-19 lifecycle).
// `_engine === "v1"` (Phase D Day 20 onward: explicit opt-out from the v2 default) → v1 triggers.
// Mutually exclusive at init; the flag becomes static after gantt initialization.
var _v2EngineActive = (function () {
	try {
		var cfg = gantt._getAutoSchedulingConfig && gantt._getAutoSchedulingConfig();
		return !!(cfg && cfg._engine === "v2");
	} catch (e) { return false; }
})();

// Phase D Day 19 — runtime deprecation warning for explicit `_engine: "v1"`.
// The default has flipped to v2 (Day 20). When a user explicitly opts back to v1
// we emit a single console.warn per gantt instance pointing at the migration guide.
(function _emitV1DeprecationWarningIfApplicable() {
	if (_v2EngineActive) return;
	try {
		var cfg = gantt._getAutoSchedulingConfig && gantt._getAutoSchedulingConfig();
		if (!cfg) return;
		// `_engine` is only treated as opt-out when set explicitly to "v1".
		// The default value comes from `auto_scheduling_config.js` (v2 since Phase D Day 20);
		// if the user did not touch the flag, no warning fires.
		if (cfg._engine !== "v1" && cfg._analysis_engine !== "v1") return;
		if (gantt.__v2DeprecationWarned) return;
		gantt.__v2DeprecationWarned = true;
		// eslint-disable-next-line no-console
		if (typeof console !== "undefined" && console.warn) {
			// eslint-disable-next-line no-console
			console.warn(
				"dhtmlxgantt: auto_scheduling._engine='v1' / _analysis_engine='v1' " +
				"is deprecated and will be removed in v9.3. " +
				"See migration guide: " +
				"https://docs.dhtmlx.com/gantt/migration#auto-scheduling-v2"
			);
		}
	} catch (e) { /* never block init on warning */ }
})();

if (_v2EngineActive) {
	// `wireRevisionTracker` installs the snapshot-cache invalidation
	// listeners (onAfterLinkAdd, onAfterTaskUpdate, etc.). Without it the
	// v2 dispatch routes (findCycles, isCircularLink, getTotalSlack, …)
	// keep returning stale results after `addLink` / `addTask` calls — the
	// 06_v2_dispatch_smoke.ts failures from the Phase D Day 20 triage.
	// `attachAutoScheduleTriggers` only wires the drag/link UI handlers;
	// snapshot invalidation is a separate concern (Phase C Day 12 / D-20).
	wireRevisionTracker(gantt);
	attachAutoScheduleTriggers(gantt);
} else {
	attachUIHandlers(gantt, linksBuilder, loopsFinder, connectedGroups);
}

// ─────────────────────────────────────────────────────────────────────────
// Phase C Day 13 — v2 dispatch wraps on the public auto-scheduling API.
//
// Captures each v1 method body, then replaces the method with a flag-gated
// dispatcher. Mirrors the Phase B pattern in `critical_path.js` (the
// `_isAnalysisV2` helper at lines 14-22 + the per-method wraps at 66-113).
//
// Phase D Day 1 update: UI trigger handlers are now exclusively v1 OR v2
// based on the init-time `_engine` flag check above. Anchor-cache lifecycle
// (D-19) is wired for v2; summary-drag under `_engine:"v2"` + `move_projects:true`
// now correctly sees pre-drag bboxes via `Snapshot.anchorOverrides`.
// ─────────────────────────────────────────────────────────────────────────

function _isPlannerV2(g) {
	if (!g._getAutoSchedulingConfig) return false;
	try {
		var cfg = g._getAutoSchedulingConfig();
		return !!(cfg && cfg._engine === "v2");
	} catch (e) {
		return false;
	}
}

function _v2_dateOrNull(epochMs) {
	return epochMs !== undefined ? new Date(epochMs) : null;
}

function _v2_normalizeId(id) {
	return String(id);
}

function _v2_rawTaskId(snap, id) {
	var task = snap.tasks.get(_v2_normalizeId(id));
	return task && task.rawId !== undefined ? task.rawId : id;
}

function _v2_rawLinkId(snap, id) {
	var normalized = _v2_normalizeId(id);
	for (var i = 0; i < snap.links.length; i++) {
		var link = snap.links[i];
		if (link.id === normalized) {
			return link.rawId !== undefined ? link.rawId : id;
		}
	}
	return id;
}

function _v2_publicGroup(snap, group) {
	return {
		tasks: group.tasks.map(function (id) { return _v2_rawTaskId(snap, id); }),
		links: group.links.map(function (id) { return _v2_rawLinkId(snap, id); })
	};
}

function _v2_publicCycle(snap, cycle) {
	return {
		tasks: cycle.tasks.map(function (id) { return _v2_rawTaskId(snap, id); }),
		links: cycle.links.map(function (id) { return _v2_rawLinkId(snap, id); })
	};
}

// ── gantt.autoSchedule(id, inclusive) ─────────────────────────────────────
var _v1_autoSchedule = gantt.autoSchedule;
gantt.autoSchedule = function (id, inclusive) {
	if (_isPlannerV2(gantt)) {
		// v2 path: engine handles selectComponent(graph, id, inclusive) per
		// D-02 inclusive semantics. Result discarded — public method has
		// `void` return shape for v1 parity.
		autoScheduleViaEngine(gantt, id, inclusive === undefined ? true : !!inclusive);
		return;
	}
	return _v1_autoSchedule.call(this, id, inclusive);
};

// ── gantt._autoSchedule(id, relations) ────────────────────────────────────
// Internal entry used by v1 UI handlers (`ui_handlers.ts:362`). v2 ignores
// the precomputed `relations` array — that's a virtual-lag-era artifact.
var _v1__autoSchedule = gantt._autoSchedule;
gantt._autoSchedule = function (id, relations) {
	if (_isPlannerV2(gantt)) {
		autoScheduleViaEngine(gantt, id, true);
		return;
	}
	return _v1__autoSchedule.call(this, id, relations);
};

// ── gantt.getConnectedGroup(id?) ──────────────────────────────────────────
var _v1_getConnectedGroup = gantt.getConnectedGroup;
gantt.getConnectedGroup = function (id) {
	if (_isPlannerV2(gantt)) {
		var snap = getOrBuildSnapshot(gantt);
		var graph = _v2_buildAnalysisGraph(snap);
		var groups = _v2_connectedComponents(graph);
		if (id === undefined) {
			// v1 parity: `getConnectedGroup()` is built from the link graph and
			// only returns components that contain at least one link. v2's
			// `connectedComponents` enumerates every vertex (singletons included),
			// so we drop the link-less ones here to keep the public shape stable.
			return groups
				.filter(function (g) { return g.links.length > 0; })
				.map(function (g) { return _v2_publicGroup(snap, g); });
		}
		// v1 quirk: project-type id returns empty group.
		if (gantt.isTaskExists(id)) {
			var task = gantt.getTask(id);
			if (gantt.isSummaryTask(task)) return { tasks: [], links: [] };
		}
		var normalizedId = _v2_normalizeId(id);
		for (var i = 0; i < groups.length; i++) {
			if (groups[i].tasks.indexOf(normalizedId) !== -1) {
				return _v2_publicGroup(snap, groups[i]);
			}
		}
		return { tasks: [], links: [] };
	}
	return _v1_getConnectedGroup.call(this, id);
};

// ── gantt.getConstraintType(task) ─────────────────────────────────────────
var _v1_getConstraintType = gantt.getConstraintType;
gantt.getConstraintType = function (task) {
	if (_isPlannerV2(gantt)) {
		if (!task) return undefined;
		// v1 quirk: non-auto-schedulable tasks return undefined.
		if (gantt._isAutoSchedulable && !gantt._isAutoSchedulable(task)) return undefined;
		// Read explicit constraint from the live task FIRST — matches v1's
		// behavior, including the `onTaskLoading` hook (`auto_scheduling.js:174`)
		// which assigns `task.constraint_type = gantt.getConstraintType(task)`
		// during parse, when the snapshot may not yet include this task.
		if (task.constraint_type) return task.constraint_type;
		// Fall back to engine-resolved type (handles D-24 project_constraint
		// inheritance and direction default per D-26).
		if (task.id === undefined || !gantt.isTaskExists || !gantt.isTaskExists(task.id)) {
			// Direction default — match v1 without consulting the snapshot.
			var cfg = gantt._getAutoSchedulingConfig();
			return (cfg && cfg.schedule_from_end) ? "alap" : "asap";
		}
		var snap = getOrBuildSnapshot(gantt);
		return _v2_resolveConstraintType(snap, task.id);
	}
	return _v1_getConstraintType.call(this, task);
};

// ── gantt.getConstraintLimitations(task) ──────────────────────────────────
var _v1_getConstraintLimitations = gantt.getConstraintLimitations;
gantt.getConstraintLimitations = function (task) {
	if (_isPlannerV2(gantt)) {
		if (!task || task.id === undefined) {
			return { earliestStart: null, earliestEnd: null, latestStart: null, latestEnd: null };
		}
		var snap = getOrBuildSnapshot(gantt);
		var limits = _v2_getConstraintLimits(snap, task.id);
		return {
			earliestStart: _v2_dateOrNull(limits.earliestStart),
			earliestEnd:   _v2_dateOrNull(limits.earliestEnd),
			latestStart:   _v2_dateOrNull(limits.latestStart),
			latestEnd:     _v2_dateOrNull(limits.latestEnd)
		};
	}
	return _v1_getConstraintLimitations.call(this, task);
};

// ── gantt.isCircularLink(link) ────────────────────────────────────────────
// v1 semantics (`loops_finder.getLoopContainingLink`) covers TWO cases:
//  - the argument IS the stored link → "is THIS link part of a cycle in the
//    current graph?";
//  - the argument carries endpoints the graph doesn't have — a link that was
//    never added, or the object an `onBeforeLinkAdd` / `onBeforeLinkUpdate`
//    handler receives (the LIVE datastore item, already mutated to the
//    proposed endpoints) → "would that link close a cycle?".
// GS-3553: the v2 route only implemented the first case, so every candidate
// came back `false`; keying the split on `isLinkExists` alone then still
// missed an EDITED stored link (its id exists, its endpoints don't).
//
// The two paths are not merged on purpose. The stored path reuses the
// memoized `findCycles(analysisGraph)` — O(1) per link once warm, so painting
// every link stays cheap, and it keeps v1's `use_progress` semantics (a cycle
// through a completed task is not reported). The candidate path is structural
// and per-call; see `core/api.ts:findCycleIfAdded`.
var _v1_isCircularLink = gantt.isCircularLink;
gantt.isCircularLink = function (link) {
	if (_isPlannerV2(gantt)) {
		if (!link) return false;
		var snap = getOrBuildSnapshot(gantt);
		var normalizedId = link.id === undefined ? undefined : _v2_normalizeId(link.id);
		var stored = normalizedId === undefined ? undefined : _v2_getSnapshotLink(snap, normalizedId);
		// Endpoints only — the link TYPE doesn't change cycle topology.
		var isStoredLink = !!stored &&
			stored.source === _v2_normalizeId(link.source) &&
			stored.target === _v2_normalizeId(link.target);
		if (!isStoredLink) {
			var durationUnit = (gantt.config && gantt.config.duration_unit) || "day";
			var candidate = _v2_buildCandidateLinkSnapshot(link, durationUnit);
			return _v2_findCycleIfAdded(snap, candidate) !== null;
		}
		var graph = _v2_buildAnalysisGraph(snap);
		var cycles = _v2_findCycles(graph);
		for (var i = 0; i < cycles.length; i++) {
			if (cycles[i].links.indexOf(normalizedId) !== -1) return true;
		}
		return false;
	}
	return _v1_isCircularLink.call(this, link);
};

// ── gantt.findCycles() ────────────────────────────────────────────────────
var _v1_findCycles = gantt.findCycles;
gantt.findCycles = function () {
	if (_isPlannerV2(gantt)) {
		var snap = getOrBuildSnapshot(gantt);
		var graph = _v2_buildAnalysisGraph(snap);
		return _v2_findCycles(graph).map(function (c) {
			return _v2_publicCycle(snap, c);
		});
	}
	return _v1_findCycles.call(this);
};

};
